import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { environmentPath, exportPath } from './api-path';
import { exportKeys, parseExport, type Export } from './entity';

/**
 * Export read query + the signed-download action.
 *
 * Exports are read-only and carry no sideloads, so this is just the
 * list read (exposed as the plain fn + queryOptions factory + suspense
 * hook triplet) plus `downloadExport`, which fetches a short-lived signed
 * URL and navigates to it (mirrors the Ember controller's `download` task).
 */

const STALE_TIME = 60_000;

/** @public */
export async function getExports(workspaceId: number): Promise<Export[]> {
  const raw = await hydraUiApi.exports.list({ params: { path: environmentPath(workspaceId) } });
  return raw.exports.map(parseExport).sort((a, b) => b.created_at.getTime() - a.created_at.getTime());
}

/**
 * Fetch a single export record. Used to poll a freshly queued export until it
 * settles (download path set, or failed).
 */
export async function getExport(workspaceId: number, exportId: string): Promise<Export> {
  const response = await hydraUiApi.exports.get({ params: { path: exportPath(workspaceId, exportId) } });
  // The generated type marks `export` optional (Go pointer), but the handler
  // always sets it on 200 — a missing record is a 404, not an empty body.
  if (response.export == null) {
    throw new Error(`Export ${exportId} missing from response body`);
  }
  return parseExport(response.export);
}

/** @public */
export const getExportsQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: exportKeys.list(workspaceId, undefined),
    queryFn: () => getExports(workspaceId),
    staleTime: STALE_TIME,
  });

interface UseExportsOptions {
  queryConfig?: QueryConfig<typeof getExportsQueryOptions>;
}

/** @public */
export const useExports = ({ queryConfig }: UseExportsOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({
    ...getExportsQueryOptions(workspaceId),
    ...queryConfig,
  });
};

/**
 * Fetch a signed download URL for an export and navigate to it. The caller is
 * responsible for the permission / accessibility gates; the backend re-enforces
 * `journeys/exports/create` and the per-export `accessible` flag independently.
 */
export async function downloadExport(workspaceId: number, exportId: string): Promise<void> {
  const response = await hydraUiApi.exports.download({ params: { path: exportPath(workspaceId, exportId) } });
  window.location.href = response.url;
}
